"use client";
"use no memo";

import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Textarea } from "../ui/textarea";
import { AlertCircle, Eye, Save, FileSignature } from "lucide-react";
import { useEngagement } from "@/contexts/EngagementContext";
import { useAuth } from "@/contexts/AuthContext";
import { apiService } from "@/services/api-service";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "./PlanningSignoffDialog";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { substituteIndependenceEntityInText } from "@/utils/independence-entity-label";
import { SHAREPOINT_CONFIG_UPDATED_EVENT } from "@/utils/sharepoint-env";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

/** Textarea that grows with content and has no scrollbar */
function AutoResizeTextarea({
  value,
  onChange,
  className,
  placeholder,
  ...props
}: React.ComponentProps<typeof Textarea>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.overflow = "hidden";
    el.style.height = `${Math.max(el.scrollHeight, 40)}px`;
  }, [value]);
  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      style={{ overflow: "hidden" }}
      rows={1}
      {...props}
    />
  );
}

/** Shape of Independence_Response.json in SharePoint Juggernaut folder */
interface IndependenceResponseEntry {
  user: { id: string; name: string; email: string; role?: string };
  engagement_id: string;
  engagement_name: string;
  submitted_at: string;
  responses: Array<{ questionId: string; response: string }>;
  /** Table textbox fields and status persisted to JSON */
  addedToEngagementFile?: string;
  competenceCapability?: string;
  threatsToIndependence?: string;
  safeguardToMitigateThreats?: string;
  remarks?: string;
}

type IndependenceResponseJson = Record<string, IndependenceResponseEntry>;

/** Independence questionnaire: section, questionId, and question text (verbatim) */
const INDEPENDENCE_QUESTIONS: { section: string; questionId: string; text: string }[] = [
  {
    section: "Financial Relationships",
    questionId: "fin-1",
    text: "I and my immediate family members (spouse, spousal equivalent or dependent (any person who received more than half of his/her support for the most recent calendar year from the relevant person)) and my Immediate relatives (spouse, parent, sibling and children any of whom is either dependent financially or consults such a person in taking decisions related to trading in securities) do not have any financial interests (shares, mutual funds, bonds, debentures, company deposits, etc.)/Security/Interest with [Entity Name and Code] and/or its Related Entities (include Holding, Shareholders holding 20-50% shareholding, Subsidiaries, Entities under Common Control, Associates including JVs), Connected Entities and other side entities (e.g. target/seller on an M&A transaction) involved.",
  },
  {
    section: "Financial Relationships",
    questionId: "fin-2",
    text: "I and my immediate family members, do not have any of the following relationships with [Entity Name and Code] and/or its Related Entities and other side entities involved:\n- Securities account\n- Insurance products\n- Loans / mortgages\n- Credit cards\n- Guarantee or security provided, in connection with the indebtedness of any third person\n- Bank accounts / fixed deposits with banks, which are under normal terms or conditions\n- Other relationships",
  },
  {
    section: "Financial Relationships",
    questionId: "fin-3",
    text: "My Close Family Members (parent, child or sibling who is not an Immediate Family Member) do not hold beneficial ownership of more than 5% in [Entity Name and Code] and/or its Related Entities.",
  },
  {
    section: "Employment Relationships",
    questionId: "emp-1",
    text: "I have not been employed by or did not serve as an Officer or Director on the board or similar management or governing body of [Entity Name and Code] and/or its Related Entities and other side entities involved.",
  },
  {
    section: "Employment Relationships",
    questionId: "emp-2",
    text: "I did not enter into employment negotiations with [Entity Name and Code] and/or its Related Entities and other side entities involved.",
  },
  {
    section: "Employment Relationships",
    questionId: "emp-3",
    text: "My immediate family members and close family members are not employed with [Entity Name and Code] and/or its Related Entities and other side entities involved.",
  },
  {
    section: "Business Relationships",
    questionId: "bus-1",
    text: "I and my Immediate Family members do not have any business relationships with [Entity Name and Code] and/or its Related Entities and other side entities involved or its management or Officer or Director or substantial shareholder.",
  },
  {
    section: "Business Relationships",
    questionId: "bus-2",
    text: "I and my immediate family members did not obtain a purchase discount which is not widely available to others, or accept a gift or hospitality, unless the value is trivial and inconsequential, from [Entity Name and Code] and/or its Related Entities and other side entities involved or its management or Officer or Director or Substantial shareholder.",
  },
  {
    section: "Close Personal Relationships",
    questionId: "per-1",
    text: "I am not aware that others with whom I have a close personal relationship owning a Material Direct or Material Indirect Financial Interest in [Entity Name and Code] or its Related Entities.",
  },
  {
    section: "Other Conflicts",
    questionId: "oth-1",
    text: "I am not aware of any other conflicts in relation to this engagement which could affect the independence, e.g., potential conflict, exclusivity, personal conflict, etc.",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-1",
    text: "I confirm that the above information is correct. If there is any change in any of the above declarations from the date of this confirmation till the 30th of September of the same calendar year, of [Entity Name and Code] and/or its Related Entities and other side entities for such financial year is concluded or end date of 'Closed Window Period', whichever is later, then I will edit my responses in the Independence tool as soon as the change occurs. Also, if I am rendering services to [Entity Name and Code] on continuous basis, then I will ensure compliance with Independence requirements till the AGM date when we cease to be the auditor of [Entity Name and Code] and/or its Related Entities.",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-2",
    text: "I will not trade in [Entity Name and Code] or its Connected Entities during the Closed Window Period.",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-3",
    text: "I hereby confirm that I have read and understood the SEBI (Prohibition of Insider Trading) Regulations, 2015 and amendments thereof and accordingly agree and confirm that the same shall be binding on me and my Immediate Relative(s).",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-4",
    text: "I hereby confirm that I have declared all the holdings and trading / other transactions in securities in my name and / or in the name of my Immediate Relative(s).",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-5",
    text: "I hereby also declare that the declarations given are true to my belief and sense. I shall be solely responsible if the declarations are found to incorrect at a later stage.",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-6",
    text: "I am aware that I shall be liable to face penal consequences as set forth including disciplinary action by the firm in the event of violation of the Code.",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-7",
    text: "I confirm that I shall handle all information on a need-to-know basis and shall not disclose any Unpublished Prive Sensitive Information (UPSI) to any person (including to any of my relatives) except in furtherance of legitimate purpose, performance of duties and discharge of legal obligation.",
  },
  {
    section: "Closing Confirmation",
    questionId: "clo-8",
    text: "I confirm that the information appearing in the information database relating to myself, my immediate relatives and persons with whom I have Material Financial Relationship is correct.",
  },
];

const COMPETENCE_CAPABILITY_POINTS = [
  "Understanding of, and practical experience with, audit engagements of a similar nature and complexity through appropriate training and participation",
  "Understanding of professional standards and regulatory and legal requirements",
  "Technical expertise, including expertise with relevant information technology and specialised areas of accounting or auditing",
  "Knowledge of relevant industries in which the client operates",
  "Ability to apply professional judgment",
  "Understanding of the firm's quality control policies and procedures",
];

const THREATS_TO_INDEPENDENCE_POINTS = [
  "Self-interest threat: financial or other interest that influences judgement or behaviour",
  "Self-review threat: not appropriately evaluating results of previous judgement or activity performed",
  "Advocacy threat: promote client/employer's position to the point that objectivity is compromised",
  "Familiarity threat: long/close relationship with client/employer",
  "Intimidation threat: deterred from acting objectively due to pressure, undue influence, etc.",
];

interface IndependenceRow {
  id: string;
  name: string;
  independenceDeclared: string;
  addedToEngagementFile: string;
  competenceCapability: string;
  threatsToIndependence: string;
  safeguardToMitigateThreats: string;
  remarks: string;
  /** User's questionnaire responses, when available from Independence_Response.json */
  responses?: Array<{ questionId: string; response: string }>;
}

type IndependenceLandingProps = {
  onSelect?: (section: string) => void;
};

function buildIndependenceRows(
  responseJson: IndependenceResponseJson | null,
  teamMembers: Array<{ id: string; name: string; email?: string }>
): IndependenceRow[] {
  const byId = new Map<string, { name: string; fromResponse?: IndependenceResponseEntry; fromTeam: boolean }>();

  // Add everyone from Independence_Response.json (declared independence)
  if (responseJson && typeof responseJson === "object") {
    for (const [key, entry] of Object.entries(responseJson)) {
      if (!entry?.user) continue;
      const id = entry.user.id || key;
      const name = entry.user.name || key;
      byId.set(id, {
        name,
        fromResponse: entry,
        fromTeam: byId.get(id)?.fromTeam ?? false,
      });
    }
  }

  // Add everyone from engagement team (access and roles); merge by id or by name if no id match
  for (const member of teamMembers) {
    const id = member.id;
    const name = (member.name || member.email || id).trim() || id;
    const existing = byId.get(id);
    if (existing) {
      existing.fromTeam = true;
    } else {
      const byName = Array.from(byId.entries()).find(
        ([_, v]) => (v.name || "").trim().toLowerCase() === name.toLowerCase()
      );
      if (byName) byName[1].fromTeam = true;
      else byId.set(id, { name, fromTeam: true });
    }
  }

  return Array.from(byId.entries())
    .map(([id, v]) => {
      const entry = v.fromResponse;
      const independenceDeclared =
        entry?.submitted_at && String(entry.submitted_at).trim() !== "" ? "Yes" : "No";
      const addedToEngagementFile = v.fromTeam ? "Yes" : "No";
      return {
        id,
        name: v.name,
        independenceDeclared,
        addedToEngagementFile,
        competenceCapability: entry?.competenceCapability ?? "",
        threatsToIndependence: entry?.threatsToIndependence ?? "",
        safeguardToMitigateThreats: entry?.safeguardToMitigateThreats ?? "",
        remarks: entry?.remarks ?? "",
        responses: entry?.responses,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

const WORKSPACE_NAME = "Independence";

export default function IndependenceLanding({ onSelect }: IndependenceLandingProps) {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  /** Bumped when the view dialog opens or archive config changes so labels re-read storage. */
  const [entityLabelTick, setEntityLabelTick] = useState(0);
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);
  const [rows, setRows] = useState<IndependenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [responseJson, setResponseJson] = useState<IndependenceResponseJson | null>(null);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [viewingRow, setViewingRow] = useState<IndependenceRow | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const resolvedIndependenceQuestions = useMemo(
    () =>
      INDEPENDENCE_QUESTIONS.map((q) => ({
        ...q,
        displayText: substituteIndependenceEntityInText(q.text),
      })),
    [entityLabelTick],
  );

  const updateRowField = (
    rowId: string,
    field: keyof Pick<IndependenceRow, "competenceCapability" | "threatsToIndependence" | "safeguardToMitigateThreats" | "remarks">,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
    );
  };

  const handleSave = async () => {
    const api = (window as any).sharePointAPI?.uploadClientJson;
    if (!api) {
      setSaveMessage("SharePoint upload is not available.");
      return;
    }
    if (!currentEngagement) {
      setSaveMessage("No engagement selected.");
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      const base = responseJson && typeof responseJson === "object" ? { ...responseJson } : {};
      for (const row of rows) {
        const key = row.name;
        const existing = base[key];
        const payload = {
          addedToEngagementFile: row.addedToEngagementFile ?? "",
          competenceCapability: row.competenceCapability ?? "",
          threatsToIndependence: row.threatsToIndependence ?? "",
          safeguardToMitigateThreats: row.safeguardToMitigateThreats ?? "",
          remarks: row.remarks ?? "",
        };
        if (existing && typeof existing === "object") {
          base[key] = { ...existing, ...payload };
        } else {
          base[key] = {
            user: { id: row.id, name: row.name, email: "", role: "" },
            engagement_id: currentEngagement.id,
            engagement_name: (currentEngagement as any).engagementName ?? (currentEngagement as any).name ?? currentEngagement.id,
            submitted_at: "",
            responses: [],
            ...payload,
          };
        }
      }
      const result = await api("Independence_Response.json", base);
      if (result?.success) {
        setResponseJson(base);
        setSaveMessage("Saved successfully.");
      } else {
        setSaveMessage(result?.error ?? "Failed to save.");
      }
    } catch (e: any) {
      setSaveMessage(e?.message ?? "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!viewingRow) return;
    setEntityLabelTick((n) => n + 1);
    const onConfigUpdated = () => setEntityLabelTick((n) => n + 1);
    window.addEventListener(SHAREPOINT_CONFIG_UPDATED_EVENT, onConfigUpdated);
    return () => window.removeEventListener(SHAREPOINT_CONFIG_UPDATED_EVENT, onConfigUpdated);
  }, [viewingRow?.id]);

  const handleViewRow = async (row: IndependenceRow) => {
    setEntityLabelTick((n) => n + 1);
    setViewLoading(true);
    setViewError(null);

    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("Independence_Response.json");
      const latestJson: IndependenceResponseJson | null =
        result?.success && result?.data && typeof result.data === "object" ? result.data : null;

      // Keep page state in sync with latest juggernaut file.
      if (latestJson) {
        setResponseJson(latestJson);
        setRows(buildIndependenceRows(latestJson, teamMembers));
      }

      const latestEntry = latestJson
        ? Object.values(latestJson).find((entry) => {
            const entryUserId = String(entry?.user?.id ?? "").trim();
            const entryUserName = String(entry?.user?.name ?? "").trim().toLowerCase();
            const targetId = String(row.id ?? "").trim();
            const targetName = String(row.name ?? "").trim().toLowerCase();
            return (entryUserId && entryUserId === targetId) || (entryUserName && entryUserName === targetName);
          })
        : null;

      setViewingRow({
        ...row,
        responses: latestEntry?.responses ?? row.responses ?? [],
      });
    } catch (e: any) {
      setViewError("Unable to load latest independence responses.");
      setViewingRow(row);
      console.error("Failed to load Independence_Response.json for view:", e);
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    if (!currentEngagement) {
      setRows([]);
      setTeamMembers([]);
      setResponseJson(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        // Load both sources in parallel so table loads for all users even if one source is restricted
        const [resResult, teamResult] = await Promise.allSettled([
          (window as any).sharePointAPI?.downloadClientJson?.("Independence_Response.json") ?? Promise.reject(new Error("SharePoint not available")),
          apiService.getEngagementTeam(currentEngagement!.id),
        ]);

        if (cancelled) return;

        const data =
          resResult.status === "fulfilled" && resResult.value?.success && resResult.value?.data && typeof resResult.value.data === "object"
            ? resResult.value.data
            : null;
        setResponseJson(data);

        const team = teamResult.status === "fulfilled" ? teamResult.value ?? [] : [];
        const normalizedTeam = (Array.isArray(team) ? team : []).map((member: any) => ({
          id: String(member.id ?? member.user_id ?? ""),
          name:
            member.user_name ??
            [member.first_name, member.last_name].filter(Boolean).join(" ")?.trim() ??
            member.email ??
            "",
          email: member.email,
        }));
        setTeamMembers(normalizedTeam);

        const built = buildIndependenceRows(data, normalizedTeam);
        setRows(built);
      } catch (e) {
        if (!cancelled) {
          console.error("IndependenceLanding load error:", e);
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [currentEngagement?.id]);

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Independence</h2>
          <p className="text-sm text-white/60">
            Competence, capability and independence of the engagement team
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSignoffDialogOpen(true)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <FileSignature className="h-4 w-4 mr-1.5" />
            Sign off
          </Button>
          <Badge className="bg-white/10 text-white/80">Planning · IND</Badge>
        </div>
      </div>

      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />

      {/* Two boxes side by side - 50% each */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border-white/10 bg-white/5 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-white">
              Matters to be considered in determining the competence and capabilities expected of the engagement team as a whole:
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="list-disc list-inside space-y-2 text-sm text-white/80">
              {COMPETENCE_CAPABILITY_POINTS.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/5 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-white">
              Circumstances creating threats to independence:
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="list-disc list-inside space-y-2 text-sm text-white/80">
              {THREATS_TO_INDEPENDENCE_POINTS.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base font-semibold text-white">
                Engagement team independence and capability
              </CardTitle>
              <p className="text-xs text-white/60">
                Record independence declarations, competence and any threats with safeguards.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {saveMessage && (
                <span className={`text-sm ${saveMessage.startsWith("Saved") ? "text-emerald-400" : "text-red-400"}`}>
                  {saveMessage}
                </span>
              )}
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || loading || rows.length === 0}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-white/5">
                  <TableHead className="text-white/80 font-medium">Name</TableHead>
                  <TableHead className="text-white/80 font-medium">Independence Declared?</TableHead>
                  <TableHead className="text-white/80 font-medium">Added to Engagement File?</TableHead>
                  <TableHead className="text-white/80 font-medium">Competence & Capability</TableHead>
                  <TableHead className="text-white/80 font-medium">Threats to Independence</TableHead>
                  <TableHead className="text-white/80 font-medium">Safeguard to Mitigate Threats</TableHead>
                  <TableHead className="text-white/80 font-medium">Remarks</TableHead>
                  <TableHead className="text-white/80 font-medium w-[80px]">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow className="border-white/10 hover:bg-white/5">
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-white/50 text-sm"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow className="border-white/10 hover:bg-white/5">
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-white/50 text-sm"
                    >
                      No entries yet. Add engagement team members to record independence and capability.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="border-white/10 hover:bg-white/5"
                    >
                      <TableCell className="text-white/90">
                        <span className="inline-flex items-center gap-1.5">
                          {row.name}
                          {row.independenceDeclared === "No" && row.addedToEngagementFile === "Yes" && (
                            <span
                              className="inline-flex text-red-400"
                              title="Non-Compliance"
                            >
                              <AlertCircle className="h-4 w-4" aria-hidden />
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={
                            row.independenceDeclared === "Yes"
                              ? "inline-block rounded-md bg-emerald-500/20 px-2.5 py-1 font-medium text-emerald-400 ring-1 ring-emerald-500/30"
                              : "inline-block rounded-md bg-red-500/20 px-2.5 py-1 font-medium text-red-400 ring-1 ring-red-500/30"
                          }
                        >
                          {row.independenceDeclared}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={
                            row.addedToEngagementFile === "Yes"
                              ? "inline-block rounded-md bg-emerald-500/20 px-2.5 py-1 font-medium text-emerald-400 ring-1 ring-emerald-500/30"
                              : "inline-block rounded-md bg-red-500/20 px-2.5 py-1 font-medium text-red-400 ring-1 ring-red-500/30"
                          }
                        >
                          {row.addedToEngagementFile}
                        </span>
                      </TableCell>
                      <TableCell className="p-1 max-w-[200px] align-top">
                        <AutoResizeTextarea
                          value={row.competenceCapability ?? ""}
                          onChange={(e) => updateRowField(row.id, "competenceCapability", e.target.value)}
                          placeholder="—"
                          className="min-h-[2.5rem] min-w-[120px] resize-none border-white/20 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-white/30"
                        />
                      </TableCell>
                      <TableCell className="p-1 max-w-[200px] align-top">
                        <AutoResizeTextarea
                          value={row.threatsToIndependence ?? ""}
                          onChange={(e) => updateRowField(row.id, "threatsToIndependence", e.target.value)}
                          placeholder="—"
                          className="min-h-[2.5rem] min-w-[120px] resize-none border-white/20 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-white/30"
                        />
                      </TableCell>
                      <TableCell className="p-1 max-w-[200px] align-top">
                        <AutoResizeTextarea
                          value={row.safeguardToMitigateThreats ?? ""}
                          onChange={(e) => updateRowField(row.id, "safeguardToMitigateThreats", e.target.value)}
                          placeholder="—"
                          className="min-h-[2.5rem] min-w-[120px] resize-none border-white/20 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-white/30"
                        />
                      </TableCell>
                      <TableCell className="p-1 max-w-[200px] align-top">
                        <AutoResizeTextarea
                          value={row.remarks ?? ""}
                          onChange={(e) => updateRowField(row.id, "remarks", e.target.value)}
                          placeholder="—"
                          className="min-h-[2.5rem] min-w-[120px] resize-none border-white/20 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-white/30"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-white/70 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                          onClick={() => handleViewRow(row)}
                          title="View responses"
                          data-allow-archival-edit={true}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View questionnaire responses dialog */}
      <Dialog open={!!viewingRow} onOpenChange={(open) => !open && setViewingRow(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden flex flex-col border-white/10 bg-slate-900 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              Independence questionnaire – {viewingRow?.name ?? ""}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-4 pr-2">
            {viewLoading && (
              <div className="text-sm text-white/60">Loading responses from Independence_Response.json…</div>
            )}
            {viewError && !viewLoading && (
              <div className="text-sm text-red-400">{viewError}</div>
            )}
            {viewingRow && (
              <>
                {resolvedIndependenceQuestions.map((q) => {
                  const resp = viewingRow.responses?.find((r) => r.questionId === q.questionId);
                  const value = resp?.response?.toLowerCase();
                  const isConfirm = value === "confirm";
                  const isReject = value === "reject";
                  return (
                    <div key={q.questionId} className="space-y-1.5">
                      <div className="flex items-start gap-2">
                        <span className="text-white/50 text-sm shrink-0 mt-0.5">
                          {q.section}
                        </span>
                      </div>
                      <p className="whitespace-pre-line text-sm text-white/90">{q.displayText}</p>
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            isConfirm
                              ? "inline-flex items-center rounded-md bg-emerald-500/25 px-2.5 py-1 text-sm font-medium text-emerald-400 ring-1 ring-emerald-500/40"
                              : isReject
                                ? "inline-flex items-center rounded-md bg-red-500/25 px-2.5 py-1 text-sm font-medium text-red-400 ring-1 ring-red-500/40"
                                : "inline-flex items-center rounded-md bg-white/10 px-2.5 py-1 text-sm text-white/50"
                          }
                        >
                          {resp ? (isConfirm ? "Confirm" : "Reject") : "—"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
